/**
 * OutreachReport — who the conveyancer email outreach is reaching, wave by wave.
 * Used by Analytics (the Outreach page) and Engagement (the Outreach view); both are handed
 * the same stored counts (functions/outreach/snapshot.js), so the two can never disagree.
 *
 * Every figure on the page is worked out in the browser from those counts
 * (utils/outreachModel.js), so the filters (waves, heads or others, which email of the series)
 * redraw at once.
 *
 * ⚠️ THREE THINGS THIS PAGE MUST KEEP DOING (the reasons are in the model and the guide):
 *   · "People" is never shown without "cannot tell" beside it. Cannot tell is NOT "probably
 *     not a person", and the page says so near the top.
 *   · HubSpot's "opened" is shown as what it is (a picture was fetched), beside our own count
 *     of what did the fetching. No estimate of "real opens" is offered: the one we tried gave
 *     a known scanner the same answer as a person, and the control for that is on the page.
 *   · Groups are compared by RATE with a range, never by count: the waves are different sizes.
 *   · "Readers the open count cannot see" (people reading with pictures off) is given as a
 *     COUNT, for the whole campaign whatever is chosen, with its check on machines beside it. A
 *     share of readers is drawn from it only when the model says the check passed
 *     (outreachModel.js readersOf). It never scales HubSpot's open count up.
 *   · ORANGE IS AN ESTIMATE, and nothing else is orange (Lee's change of brief, 7 Oct 2026:
 *     likely numbers, marked as estimates). "How many probably read it" gives the range first,
 *     says "Estimate" and "untested", says what it rests on and assumes, and is never put in the
 *     ladder or a total with counts (outreachModel.js estimatesOf).
 *
 * Counts only. No name, address or contact id reaches this component.
 */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FaBookOpen, FaSyncAlt, FaTimes } from 'react-icons/fa';
import { Panel } from '../Panel';
import CompositionBar from '../CompositionBar';
import RankedList from '../RankedList';
import HeatmapGrid from '../HeatmapGrid';
import SeriesChart from '../SeriesChart';
import IntervalPlot from '../IntervalPlot';
import InfoNotice from '../InfoNotice';
import LoadingState from '../LoadingState';
import { EmptyState } from '../EmptyState';
import { Button } from '../Button';
import { Checkbox } from '../Form';
import { SegmentedControl } from '../Toolbar';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { fmt, dayLabel } from '../../../utils/analyticsFormat';
import {
  DEFAULT_FILTERS, LAG_BUCKETS, METRICS, MIN_READERS, MIN_CHECK, CHECK_CEILING, SMALL_BASE, normaliseFilters, select, ladder, basisOf, hasEmailFigures, metricsFor, compare,
  acrossSeries, patternLabel, picturesOf, emailRows, emailShown, wavesOf, stepsOf, freshness, formatRate, wilson, usableCube,
  arrivalOf, SPAM_LIMIT, DELIVERED_FLOOR, estimatesOf, estimateRows, IMPLAUSIBLE_SHARE, estimateText, estimateMiddle, estimateAbout, floorOf,
} from '../../../utils/outreachModel';
import OutreachGuide from './OutreachGuide';
import './OutreachReport.scss';

// One meaning per colour, everywhere on the page.
const HATCH = 'repeating-linear-gradient(135deg, #94a3b8 0 3px, #e2e8f0 3px 6px)';
export const BAND_COLORS = { person: '#15803d', unsure: HATCH, machine: '#cbd5e1' };
const KIND_LABEL = {
  twin: ['Both links of one email within 10 seconds', 'A scanner opens every link in an email; a person clicks one.'],
  datacentre: ['From a cloud or security provider’s network', 'Proofpoint, AWS, Azure and the like.'],
  automated: ['A browser run by a program', 'It says so itself: headless, or driven by automation.'],
  abroad: ['Outside the UK', 'These emails go to conveyancers in England and Wales.'],
};
const LAG_LABEL = { '0-2m': 'under 2 min', '2-10m': '2 to 10 min', '10-60m': '10 to 60 min', '1-24h': '1 to 24 hours', '1-7d': '1 to 7 days', '7d+': 'over a week' };
const ROLE_OPTIONS = [{ value: 'all', label: 'Everyone' }, { value: 'head', label: 'Heads' }, { value: 'other', label: 'Others' }];
// ORANGE IS AN ESTIMATE, wherever it appears on this page, and nothing else is orange.
export const ESTIMATE_COLOR = '#c2410c';
const CONFIDENCE_WORD = { low: 'low', 'very-low': 'very low' };
// A visit matched to its email: had a picture loaded before the click? Green: one had. Straw:
// the only open is at the click or after. Amber: none, or only a machine's.
const READER_COLORS = { program: '#15803d', apple: '#7c93b3', browser: '#86b79a', atClick: '#e2c275', after: '#f0dca8', machine: '#d08a4e', none: '#b45309' };
const READER_LABEL = {
  program: 'A mail program had loaded a picture', apple: 'Apple’s mail service had', browser: 'Something calling itself a browser had',
  atClick: 'An open only at the moment of the click', after: 'An open only later',
  machine: 'Only a machine had opened it', none: 'No open at all',
};
const FATE_SEGMENTS = [
  { key: 'fateRead', label: 'Kept open 8 seconds or more', color: '#15803d', help: 'HubSpot times how long the email stayed open. The nearest thing to “read”.' },
  { key: 'fateSkimmed', label: 'Open for 2 to 8 seconds', color: '#3f9463' },
  { key: 'fateGlanced', label: 'Open for under 2 seconds', color: '#86b79a', help: 'A glance, or a preview pane passing over it.' },
  { key: 'fateUntimed', label: 'Opened, no time recorded', color: '#b7d3c2' },
  { key: 'fateClickOnly', label: 'Clicked, with no open recorded', color: '#b45309', help: 'Read with pictures off, and proved it by clicking. HubSpot counts these as opened.' },
  { key: 'fateMachine', label: 'Only a machine opened it', color: '#cbd5e1', help: 'Every open was one HubSpot marked as automated.' },
  { key: 'fateNone', label: 'No sign of an open', color: '#eef2f6', help: 'Not opened, or read with pictures off and not clicked. Nothing can tell these two apart.' },
];
const PROGRAM_LABEL = { outlook: 'Outlook', phone: 'A phone’s mail app', webmail: 'Webmail', mac: 'Apple Mail on a Mac', 'other-program': 'Another mail program' };

/** Visits are not people, so they are given for every 1,000 sent to, not as a share. */
const perThousand = (v, of) => { const x = (v / of) * 1000; return x >= 10 ? String(Math.round(x)) : x.toFixed(1).replace(/\.0$/, ''); };
const oneIn = (v, of) => (v && of && v / of < 0.02 ? ` · 1 in ${fmt(Math.round(of / v))}` : '');
const plural = (n, one, many) => `${fmt(n)} ${n === 1 ? one : many || `${one}s`}`;
const dateTime = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) : '—');

/** A titled panel that is also a named region, so each part of the page can be found by name. */
const Block = ({ title, children, ...panel }) => (
  <section aria-label={title} className="outreach__block">
    <Panel title={title} {...panel}>{children}</Panel>
  </section>
);
Block.propTypes = { title: PropTypes.string.isRequired, children: PropTypes.node };

/* ── the filters ─────────────────────────────────────────────────────────────────────── */
const Filters = ({ cube, filters, onChange }) => {
  const waves = wavesOf(cube);
  const steps = stepsOf(cube);
  const toggle = (wave) => onChange({ ...filters, waves: filters.waves.includes(wave) ? filters.waves.filter((w) => w !== wave) : [...filters.waves, wave] });
  return (
    <div className="outreach__filters" role="group" aria-label="Filters">
      <div className="outreach__filter">
        <span className="outreach__filter-label" id="outreach-waves">Waves</span>
        <div className="outreach__waves" role="group" aria-labelledby="outreach-waves">
          <button type="button" className="outreach__wave outreach__wave--all" aria-pressed={!filters.waves.length} onClick={() => onChange({ ...filters, waves: [] })}>All</button>
          {waves.map((w) => (
            <button
              key={w.wave}
              type="button"
              className="outreach__wave"
              aria-pressed={filters.waves.includes(w.wave)}
              aria-label={`Wave ${w.wave}: ${fmt(w.total)} people, ${w.sent ? plural(w.emailsSent, 'email') + ' so far' : 'not sent yet'}`}
              title={`Wave ${w.wave}: ${fmt(w.total)} people (${fmt(w.heads)} heads, ${fmt(w.others)} others). ${w.sent ? `${plural(w.emailsSent, 'email')} so far.` : 'Not sent yet.'}`}
              disabled={!w.sent}
              onClick={() => toggle(w.wave)}
            >
              <b>{w.wave}</b>
              <small>{fmt(w.total)}</small>
              <span className="outreach__wave-dots" aria-hidden="true">{[1, 2, 3].map((s) => <i key={s} className={s <= w.emailsSent ? 'on' : ''} />)}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="outreach__filter">
        <span className="outreach__filter-label">Sent to</span>
        <SegmentedControl value={filters.role} onChange={(role) => onChange({ ...filters, role })} options={ROLE_OPTIONS} />
      </div>
      <div className="outreach__filter">
        <span className="outreach__filter-label">Email in the series</span>
        <SegmentedControl
          value={filters.step}
          onChange={(step) => onChange({ ...filters, step })}
          options={[{ value: 'all', label: 'All' }, ...steps.map((s) => ({ value: s, label: String(s) }))]}
        />
      </div>
    </div>
  );
};
Filters.propTypes = { cube: PropTypes.object.isRequired, filters: PropTypes.object.isRequired, onChange: PropTypes.func.isRequired };

/* ── the ladder: delivered → opened → clicked → visited → people ─────────────────────────── */
const Ladder = ({ rungs }) => {
  const main = rungs.filter((r) => !r.among);
  const among = rungs.filter((r) => r.among);
  const top = main[0].value || 0;
  // Square-root scale: on a straight one, ten visits beside eight thousand emails are invisible.
  const width = (v) => (top && v ? `${Math.max(Math.sqrt(v / top) * 100, 1.2)}%` : '0%');
  const people = main.find((r) => r.key === 'people');
  return (
    <div className="outreach-ladder">
      <ol className="outreach-ladder__rungs" aria-label="From sent to a visit by a person">
      {main.map((r) => (
        <li className={`outreach-ladder__rung outreach-ladder__rung--${r.key}`} key={r.key} aria-label={r.label}>
          <div className="outreach-ladder__label">
            <strong>{r.label}</strong>
            {r.caveat && <small>{r.caveat}</small>}
          </div>
          <div className="outreach-ladder__bar" aria-hidden="true">
            {r.split ? (
              <span className="outreach-ladder__fill outreach-ladder__fill--split" style={{ width: width(r.value) }}>
                {['person', 'unsure', 'machine'].map((b) => (r.split[b] ? <i key={b} style={{ flex: r.split[b], background: BAND_COLORS[b] }} /> : null))}
              </span>
            ) : r.upTo != null ? (
              <>
                <span className="outreach-ladder__fill outreach-ladder__fill--upto" style={{ width: width(r.upTo) }} />
                <span className="outreach-ladder__fill outreach-ladder__fill--person" style={{ width: width(r.value) }} />
              </>
            ) : (
              <span className={`outreach-ladder__fill outreach-ladder__fill--${r.source === 'HubSpot' ? 'hubspot' : 'ours'}`} style={{ width: width(r.value) }} />
            )}
          </div>
          <div className="outreach-ladder__figure">
            {r.value == null ? <strong>—</strong> : r.upTo != null ? (
              <>
                <strong>{fmt(r.value)} <span>to {fmt(r.upTo)}</span></strong>
                {r.of ? <small>{perThousand(r.value, r.of)} to {perThousand(r.upTo, r.of)} for every 1,000 {r.ofLabel}</small> : null}
              </>
            ) : r.split ? (
              <>
                <strong>{fmt(r.value)}</strong>
                <small>visits of every kind</small>
              </>
            ) : (
              <>
                <strong>{fmt(r.value)}</strong>
                {r.of ? <small>{formatRate(r.value / r.of)} of {r.ofLabel}{oneIn(r.value, r.of)}</small> : <small>{r.source}</small>}
              </>
            )}
          </div>
        </li>
      ))}
      </ol>
      <p className="outreach-ladder__scale">Bars are drawn on a square-root scale, so the small numbers at the foot can be seen at all. Read the figures, not the lengths.</p>
      {people && people.value > 0 && (
        <div className="outreach-ladder__among">
          <span>Of the {plural(people.value, 'visit')} by people:</span>
          {among.map((r) => <span className="outreach__chip" key={r.key}><b>{fmt(r.value)}</b> {r.label.toLowerCase()}</span>)}
        </div>
      )}
    </div>
  );
};
Ladder.propTypes = { rungs: PropTypes.array.isRequired };

/* ── minutes from the send to the visit: three rows of counts ────────────────────────────── */
const Timing = ({ timing }) => {
  // Each row is drawn to its own tallest bar: the point is WHEN each band arrives, and beside
  // 194 machines a row of 10 people would be flat. The counts under the bars are the truth.
  const maxOf = (band) => Math.max(1, ...LAG_BUCKETS.map((k) => timing[band][k]));
  const rows = [['person', 'People'], ['unsure', 'Cannot tell'], ['machine', 'Machines']];
  return (
    <table className="outreach-timing">
      <caption>How long after the email went out each visit arrived</caption>
      <thead><tr><th scope="col" />{LAG_BUCKETS.map((k) => <th scope="col" key={k}>{LAG_LABEL[k]}</th>)}</tr></thead>
      <tbody>
        {rows.map(([band, label]) => (
          <tr key={band}>
            <th scope="row">{label}</th>
            {LAG_BUCKETS.map((k) => {
              const n = timing[band][k];
              return (
                <td key={k}>
                  <span className="outreach-timing__cell">
                    <i style={{ height: `${n ? Math.max((n / maxOf(band)) * 100, 6) : 0}%`, background: BAND_COLORS[band] }} />
                  </span>
                  <span className={n ? '' : 'outreach__muted'}>{n ? fmt(n) : '–'}</span>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
Timing.propTypes = { timing: PropTypes.object.isRequired };

/* ── one comparison: a chart, the sentence under it, and its notes ───────────────────────── */
const Comparison = ({ title, result, color }) => {
  const all = result.rows.reduce((a, r) => ({ k: a.k + (r.k || 0), n: a.n + (r.n || 0) }), { k: 0, n: 0 });
  const together = wilson(all.k, all.n);
  return (
    <div className="outreach-compare">
      <h4 className="outreach__subhead">{title}</h4>
      <IntervalPlot
        rows={result.rows}
        color={color}
        of={result.metric.of}
        reference={together && result.rows.length > 1 ? { value: together.rate, label: 'All together' } : undefined}
        activeKeys={result.verdict.apart.map((a) => a.key)}
        emptyText="Nothing sent to compare yet."
      />
      {result.rows.length > 0 && <p className="outreach-compare__verdict">{result.verdict.text}</p>}
      {result.notes.map((n) => <p className="outreach__note" key={n}>{n}</p>)}
    </div>
  );
};
Comparison.propTypes = { title: PropTypes.string.isRequired, result: PropTypes.object.isRequired, color: PropTypes.string };

/* ── how many probably read it: an estimate, never a count ───────────────────────────────── */
// The range first, and rounded: an estimate given to the nearest percent claims more than it
// knows. The wording is the model's (estimateText), so every place that shows one agrees.
const twoFigures = (n) => { if (!n) return '0'; const m = 10 ** Math.max(0, Math.floor(Math.log10(n)) - 1); return fmt(Math.round(n / m) * m); };

const VERDICT_WORDS = {
  few: 'Too few on one side to tell: 20 are needed on each.',
  'none-found': 'No large difference found.',
  differs: 'They differ. Confidence is very low.',
  cannot: 'Cannot be worked out on these figures.',
};
const ofSide = (s) => `${fmt(s.k)} of ${fmt(s.n)}`;

/**
 * The checks on the estimate. ⚠️ Both are weak, and say so: "no large difference found" is
 * never support. The three middles are always set side by side. A check whose figures were not
 * stored says "not measured", never a pass.
 */
const EstimateChecks = ({ checks }) => {
  if (!checks) return null;
  const { timing, clickTiming, definitions } = checks;
  const timed = (title, t, what) => (
    <li>
      <strong>{title}</strong>{' '}
      {!t ? 'Not measured on these figures.' : (
        <>
          Within ten minutes of the email arriving: {ofSide(t.early)} {what}. Later: {ofSide(t.later)}.
          {t.untimed ? ` (${fmt(t.untimed)} could not be timed.)` : ''}{' '}
          <em className={`outreach-est__verdict outreach-est__verdict--${t.verdict}`}>{VERDICT_WORDS[t.verdict]}</em>
        </>
      )}
    </li>
  );
  return (
    <div className="outreach-est__checks" role="group" aria-label="Checks on the estimate">
      <h4 className="outreach__subhead">Checks on the estimate</h4>
      <p className="outreach__note">
        Their pass lines were fixed before any of these figures existed. <strong>Both kinds are weak:</strong> they can only catch a large difference, so “no large difference found” is not support for the estimate.
      </p>
      <ul className="outreach__points">
        {timed('Those who unsubscribed at once, against later.', timing, 'had such an open')}
        {timed('The clicks HubSpot kept, at once against later.', clickTiming, 'had a mail-program open before the click')}
        <li>
          <strong>If “an open” is defined differently.</strong>{' '}
          {definitions.middles.map((m, i) => (
            <span key={m.key} className="outreach-est__middle">
              {i === 0 ? 'By ' : 'Counting '}{m.label}: {m.same ? 'the same figures as the first on this build, so not a separate reading'
                : m.mid == null ? 'cannot be worked out'
                // A middle over half is not offered as a likely figure anywhere on the page: here it is named as that.
                  : m.mid > IMPLAUSIBLE_SHARE ? <span className="outreach-est__figure">over {formatRate(IMPLAUSIBLE_SHARE)}, which is hard to believe</span>
                    : <span className="outreach-est__figure">about {estimateAbout(m.mid)}</span>}{'. '}
            </span>
          ))}
          <em className={`outreach-est__verdict outreach-est__verdict--${definitions.verdict}`}>
            {definitions.verdict === 'none-found' ? `No large difference found: the other ${definitions.readings === 1 ? 'reading falls' : 'two fall'} inside the estimate’s own range.`
              : definitions.verdict === 'differs' ? 'They differ: one falls outside the estimate’s own range. Confidence is very low.'
                : definitions.readings === 0 ? 'There is no second reading to compare on these figures.' : VERDICT_WORDS.cannot}
          </em>
        </li>
      </ul>
    </div>
  );
};
EstimateChecks.propTypes = { checks: PropTypes.object };

/* ── at least, on the record: a count, never an estimate ─────────────────────────────────── */
const Floor = ({ floor }) => {
  const [withClicks, setWithClicks] = useState(false);
  if (!floor) {
    return <p className="outreach__plain outreach__plain--flat"><strong>Not counted on these figures.</strong> It is counted from each recipient’s own opens and unsubscribes, and they were not read on the last build.</p>;
  }
  const shown = withClicks ? floor.withClicks : floor.share;
  return (
    <div className="outreach-floor">
      <div className="outreach__headline">
        <div>
          <span className="outreach__big">{fmt(shown.k)}</span>
          <span className="outreach__big-label">
            of {fmt(floor.delivered)} emails delivered were in front of a person, at least{shown.rate != null ? ` (${formatRate(shown.rate)})` : ''}<br />
            <small>{withClicks ? 'on the record, with the clicks HubSpot kept added: not all of those are people' : 'on the record. A count, not an estimate'}</small>
          </span>
        </div>
      </div>
      <ul className="outreach__points">
        <li><strong>{fmt(floor.program)}</strong>: the person’s own mail program loaded a picture. An open within two seconds of a click is not counted: that is the click.</li>
        <li><strong>{fmt(floor.unsub)}</strong>: {floor.unsubsRead ? 'they unsubscribed using the link inside the email. A scanner that follows the link does not unsubscribe: HubSpot’s page asks a person to confirm.' : 'who unsubscribed was not read on the last build, so this is the mail-program part alone.'}</li>
        <li>
          <strong>Beside it, not in it: {fmt(floor.alsoClicked)}</strong> more had neither sign and a click that HubSpot kept. Not all of those are people.{' '}
          {floor.keptClicks && floor.keptClicks.total > 0 && (
            <>Across the whole campaign, {fmt(floor.keptClicks.early)} of the {fmt(floor.keptClicks.total)} clicks HubSpot kept came within ten minutes of the email arriving, and {fmt(floor.keptClicks.earlyNoOpen)} of those had no mail-program open before the click, which is what a link scanner does and not what most readers do. A person with pictures off can click at once too, so this is a pattern, not a count of machines.{' '}</>
          )}
          {floor.alsoClicked > 0 && (
            <Checkbox className="outreach-floor__add" checked={withClicks} onChange={(e) => setWithClicks(e.target.checked)} label="Add them to the figure above" />
          )}
        </li>
        {floor.people && (
          <li><strong>People:</strong> {fmt(floor.people.shown)} of {fmt(floor.people.people)} people{floor.people.share.rate != null ? ` (${formatRate(floor.people.share.rate)})` : ''} had at least one email on the record, each counted once.{floor.people.alsoClicked ? ` ${fmt(floor.people.alsoClicked)} more only clicked.` : ''}</li>
        )}
        {floor.noDelivery > 0 && <li>{plural(floor.noDelivery, 'more sign')} had no delivery read for {floor.noDelivery === 1 ? 'it' : 'them'}, and {floor.noDelivery === 1 ? 'is' : 'are'} not counted.</li>}
        {floor.ownLeftOut > 0 && <li>{plural(floor.ownLeftOut, 'email')} to our own addresses had such a sign or a click, and {floor.ownLeftOut === 1 ? 'is' : 'are'} left out: a colleague’s test is not a recipient’s.</li>}
      </ul>
      <p className="outreach__note">
        “On the record”, not “proven”: the first part rests on reading a program’s name. Left out: Apple’s background fetches, “browser name” opens, opens HubSpot marked as machines, and the mail program’s own unsubscribe button. The estimate is not built from this figure, and the two are never added together.
      </p>
    </div>
  );
};
Floor.propTypes = { floor: PropTypes.object };

const Estimate = ({ est, rows, onGuide }) => {
  if (!est) {
    return <p className="outreach__plain outreach__plain--flat"><strong>No estimate on these figures.</strong> It is worked out from each recipient’s own opens, clicks and unsubscribes, and they were not read on the last build.</p>;
  }
  if (!est.ok) {
    return (
      <p className="outreach__plain outreach__plain--flat">
        <strong>No estimate yet.</strong>{' '}
        {est.why === 'not-read' ? 'Who unsubscribed was not read on the last build, and the estimate is scaled from them.'
          : est.why === 'thin-count' ? `Too few counted opens to estimate for this choice: ${plural(est.counted, 'email')} of ${fmt(est.delivered)} opened by a person’s own mail program.`
            : est.why === 'over' ? `For this choice the scaling comes to more emails than were delivered (${fmt(est.counted)} counted of ${fmt(est.delivered)}), so it does not fit and none is given.`
              : `It is scaled from the people who unsubscribed using the link in an email, and there are too few of them yet (${fmt(est.baseN)}, ${fmt(est.baseSeen)} of them with an open recorded).`}
      </p>
    );
  }
  const { share, emails, base, second, flags } = est;
  const pos = (x) => `${Math.min(Math.max(x, 0), 1) * 100}%`;
  return (
    <div className={`outreach-est outreach-est--${est.confidence}`}>
      <InfoNotice variant="warning">
        <strong>An estimate, not a count{est.untested ? ', and untested' : ''}.</strong>{' '}
        {est.untested ? 'Nobody has yet checked it against mailboxes where the answer is known. A seed test would: one of these emails sent to twenty or thirty mailboxes we control, with a note of who read it. ' : ''}
        Read the range, not the middle. It does not show which wave or email did better.
      </InfoNotice>

      <div className="outreach__headline outreach-est__headline">
        <div>
          <span className="outreach__big outreach-est__figure">{estimateText(share)}</span>
          <span className="outreach__big-label">
            <span className="outreach-est__tag">Estimate</span> of the emails delivered were probably opened by a person<br />
            <small>{estimateMiddle(share) ? `most likely about ${estimateMiddle(share)} · ` : ''}{twoFigures(emails.low)} to {twoFigures(emails.high)} of {fmt(est.delivered)} emails · confidence: {CONFIDENCE_WORD[est.confidence]}</small>
          </span>
        </div>
      </div>

      <div className="outreach-est__scale" role="img" aria-label={`Of ${fmt(est.delivered)} emails delivered: ${fmt(est.counted)} counted as opened by a person’s own mail program (${formatRate(est.counted / est.delivered)}); estimate ${estimateText(share)}${estimateMiddle(share) ? `, most likely about ${estimateMiddle(share)}` : ''}`}>
        <div className="outreach-est__track">
          <i className="outreach-est__band" style={{ left: pos(share.low), width: `${Math.max((share.high - share.low) * 100, 0.6)}%` }} />
          <i className="outreach-est__mid" style={{ left: pos(share.mid) }} />
          <i className="outreach-est__counted" style={{ width: pos(est.counted / est.delivered) }} />
        </div>
        <div className="outreach-est__axis" aria-hidden="true"><span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100% of delivered</span></div>
        <div className="outreach-est__key" aria-hidden="true">
          <span><i className="outreach-est__key-counted" />Counted: {fmt(est.counted)} opened by a person’s own mail program ({formatRate(est.counted / est.delivered)})</span>
          <span><i className="outreach-est__key-band" />Estimate: probably opened by a person</span>
        </div>
      </div>

      {flags.length > 0 && (
        <InfoNotice variant="warning">
          <strong>Confidence is very low for this choice.</strong>
          {flags.includes('implausible') ? ` The middle of the estimate is over ${formatRate(IMPLAUSIBLE_SHARE)} of everything delivered, which is hard to believe of an email from a stranger.` : ''}
          {flags.includes('basesPart') ? ' The two readings it could be scaled from do not agree with each other.' : ''}
          {flags.includes('thin') ? ` It rests on only ${plural(est.counted, 'counted open')}.` : ''}
          {flags.includes('timingDiffers') ? ' People who unsubscribed at once and people who did so later differ in how often a picture had loaded.' : ''}
          {flags.includes('clickTimingDiffers') ? ' Clicks made at once and clicks made later differ in how often a picture had loaded first.' : ''}
          {flags.includes('definitionSwings') ? ' Defining “an open” differently moves the estimate outside its own range.' : ''}
          {flags.includes('firmsClustered') ? ` Measured by firm, the people it is scaled from are far from separate readings (the figure is ${est.measured.design}, where 1 would be fully separate): one or a few firms hold much of that group, so the range is very wide.` : ''}
        </InfoNotice>
      )}

      <ul className="outreach__points">
        <li>
          <strong>What is counted.</strong> {fmt(est.counted)} emails where a person’s own mail program loaded a picture. Opens HubSpot marked as machines, Apple’s background fetches, “browser name” opens and most opens made by a click are left out.
        </li>
        <li>
          <strong>What it is scaled by.</strong> Of the {fmt(base.n)} people who unsubscribed using the link inside an email, and so had it open, {fmt(base.k)} had such an open recorded at some time: about 1 in {Math.round(1 / base.rate)}. The rest had no such open recorded.
          {est.ownLeftOut > 0 ? ` (${plural(est.ownLeftOut, 'of our own addresses is', 'of our own addresses are')} left out of that group.)` : ''}
          {second ? ` The clicks HubSpot kept give a second reading: ${fmt(second.k)} of ${fmt(second.n)}.` : ''}
        </li>
        <li>
          <strong>Why the range is so wide.</strong> People in one firm share one mail policy, so {fmt(base.n)} people are far fewer than {fmt(base.n)} separate readings. The range is drawn as if there were {fmt(Math.round(base.n / est.allowance))}.
          {est.measured && est.measured.design != null ? ` Measured by firm, they fell into ${plural(est.measured.groups, 'group')}, which alone would mean dividing by ${est.measured.design}; that covers one source of doubt only, so the wider of it and halving is used until the estimate has been tested.` : ''}
          {est.measured && est.measured.design == null ? ` Measured by firm, they fell into ${plural(est.measured.groups, 'group')}: too few, or too alike, to work a figure out, so halving is used.` : ''}
          {share.high > IMPLAUSIBLE_SHARE ? ` The top of it, over ${formatRate(IMPLAUSIBLE_SHARE)} of everything delivered, is hard to believe of an email from a stranger.` : ''}
        </li>
        <li>
          <strong>What it assumes.</strong> That people who unsubscribe show pictures about as often as people who read quietly. That cannot be checked from these figures.
        </li>
      </ul>

      <EstimateChecks checks={est.checks} />

      <div className="outreach__grid outreach__grid--3 outreach-est__rows">
        {[['wave', 'Estimate by wave'], ['role', 'Estimate for heads and others'], ['step', 'Estimate by email of the series']].map(([dim, title]) => (
          <div className="outreach-compare" key={dim}>
            <h4 className="outreach__subhead">{title}</h4>
            <IntervalPlot estimate rows={rows[dim]} color={ESTIMATE_COLOR} max={1} emptyText="No estimate for this choice." />
          </div>
        ))}
      </div>
      <p className="outreach__note">
        One rate, for the whole campaign, is applied to every wave and email, so the order above is the order of the counted opens: it shows how wide each estimate is, not which did better.
        “Responded” is not estimated: the visits by people, and those we cannot tell, are already a range.{' '}
        <button type="button" className="outreach__link" onClick={onGuide}>How the estimate is made</button>
      </p>
    </div>
  );
};
Estimate.propTypes = { est: PropTypes.object, rows: PropTypes.object.isRequired, onGuide: PropTypes.func.isRequired };

/* ── are the emails arriving? ─────────────────────────────────────────────────────────── */
const Arrival = ({ arrival, proven, programs, topProgram }) => {
  const a = arrival;
  if (a.verdict === 'none') {
    return (
      <p className="outreach__plain outreach__plain--flat">
        <strong>Nothing to judge yet for this choice.</strong>{' '}
        {a.fresh > 0 ? `${plural(a.fresh, 'email')} sent in the last day ${a.fresh === 1 ? 'is' : 'are'} still being delivered.` : 'HubSpot’s delivery figures were not read for these emails.'}
      </p>
    );
  }
  return (
    <div className="outreach-arrival">
      <div className="outreach__headline">
        <div>
          <span className={`outreach__big${a.reasons.includes('refused') ? ' outreach__big--hidden' : ''}`}>{formatRate(a.deliveredRate)}</span>
          <span className="outreach__big-label">accepted by the receiving mail systems<br /><small>{fmt(a.delivered)} of {fmt(a.sent)} sent · {fmt(a.bounced)} bounced ({formatRate(a.bounceRate)})</small></span>
        </div>
        <div>
          <span className={`outreach__big${a.reasons.includes('spam') ? ' outreach__big--hidden' : ''}`}>{a.spam == null ? '—' : fmt(a.spam)}</span>
          <span className="outreach__big-label">reported as spam by a recipient<br /><small>{a.spamRate == null ? 'not read' : `${a.spam === 0 ? 'none' : `1 in ${fmt(Math.round(a.delivered / a.spam))}`} of those delivered`} · mailbox providers ask for under 1 in {fmt(Math.round(1 / SPAM_LIMIT))}</small></span>
        </div>
        <div>
          <span className="outreach__big">{a.unsubscribed == null ? '—' : fmt(a.unsubscribed)}</span>
          <span className="outreach__big-label">unsubscribed<br /><small>each one somebody who saw the email and said no</small></span>
        </div>
      </div>
      <p className="outreach__plain outreach-arrival__verdict">
        {a.verdict === 'fine' ? (
          <><strong>Nothing here suggests the emails are being refused or reported.</strong> The few proven visits are a measure of how many clicked through, not of how many arrived.</>
        ) : (
          <>
            <strong>Worth a look.</strong>
            {a.reasons.includes('refused') ? ` Fewer than ${formatRate(DELIVERED_FLOOR)} of these emails were accepted.` : ''}
            {a.reasons.includes('spam') ? ` More were reported as spam than the 1 in ${fmt(Math.round(1 / SPAM_LIMIT))} mailbox providers allow a bulk sender.` : ''}
          </>
        )}
      </p>
      <ul className="outreach__points">
        {a.bounceByStep.length > 1 && (
          <li>
            {a.bounceByStep[a.bounceByStep.length - 1].rate < a.bounceByStep[0].rate ? (
              <><strong>Bounces fall after the first email:</strong> {a.bounceByStep.map((b) => `email ${b.step} ${formatRate(b.rate)}`).join(' · ')}. A first email to a new list finds the dead addresses, and they are not sent to again.</>
            ) : (
              <><strong>Bounces by email of the series:</strong> {a.bounceByStep.map((b) => `email ${b.step} ${formatRate(b.rate)}`).join(' · ')}. They would be expected to fall after the first email, and have not.</>
            )}
          </li>
        )}
        {a.reached && a.reached.of > 0 && (
          <li>
            <strong>It reached their mail system.</strong> For {fmt(a.reached.n)} of the {fmt(a.reached.of)} people ({formatRate(a.reached.n / a.reached.of)}), something at their end fetched a picture from an email at least once: their firm’s mail security, or their own mail program. That only happens to an email that has arrived. It is <strong>not</strong> a count of people who opened it: mail security fetches the pictures whether anyone reads the email or not.
          </li>
        )}
        {a.keptOpen != null && (
          <li>
            <strong>Signs a person saw it:</strong> {plural(a.keptOpen, 'email')} shown by a person’s own mail program with pictures on{a.linkUnsubs != null ? `; ${plural(a.linkUnsubs, 'person', 'people')} unsubscribed using the link inside the email` : ''}; {fmt(a.clicked)} clicked{a.replied != null ? `; ${fmt(a.replied)} replied` : ''}.
            {programs > 0 ? ` Our own logo has been fetched ${fmt(programs)} times by people’s mail programs${topProgram ? `, ${topProgram} most of all` : ''}.` : ''}
          </li>
        )}
        <li>
          <strong>What this cannot say: inbox or junk folder.</strong> No sender is told. An email sitting in a junk folder would show almost none of the signs in the line above.
        </li>
        <li>
          <strong>“{plural(proven, 'visit')} by people” is not “{plural(proven, 'email')} got through”.</strong> It is the few who clicked through and whom we can prove were people. Most readers leave no trace at all: the next panel measures that.
        </li>
      </ul>
      <p className="outreach__note">
        “Worth a look” is said when fewer than {formatRate(DELIVERED_FLOOR)} were accepted, or more than 1 in {fmt(Math.round(1 / SPAM_LIMIT))} of those delivered were reported as spam (the line Google and Yahoo publish for bulk senders); otherwise it is not.
        {' '}HubSpot’s own figures for {plural(a.emails, 'email')} sent more than a day ago.
        {a.fresh > 0 ? ` ${plural(a.fresh, 'email')} sent since ${a.fresh === 1 ? 'is' : 'are'} still being delivered and ${a.fresh === 1 ? 'is' : 'are'} left out.` : ''}
        {a.leftOut > 0 ? ` ${plural(a.leftOut, 'email')} that cannot be divided by this choice ${a.leftOut === 1 ? 'is' : 'are'} left out too.` : ''}
      </p>
    </div>
  );
};
Arrival.propTypes = { arrival: PropTypes.object.isRequired, proven: PropTypes.number.isRequired, programs: PropTypes.number, topProgram: PropTypes.string };

/* ── readers the open count cannot see ──────────────────────────────────────────────────── */
const inTen = (x) => String(Math.round(x * 10));
const said = (parts) => parts.filter(Boolean).join(' · ');

const Readers = ({ readers, recipients, onGuide }) => {
  if (!readers) {
    return (
      <p className="outreach__plain outreach__plain--flat">
        <strong>Not measured on these figures.</strong> It takes each recipient’s own opens and clicks from HubSpot, and they were not read on the last build.
        Our own records cannot answer it alone: our logo has one address for every email, so a fetch cannot be tied to a reader.{' '}
        <button type="button" className="outreach__link" onClick={onGuide}>How it is measured</button>
      </p>
    );
  }
  const { bands, statement, soon } = readers;
  const p = bands.person;
  const m = bands.machine;
  const rowsOf = [['person', 'People, on the evidence'], ['unsure', 'Cannot tell'], ['machine', 'Machines: the check']];
  const notMatched = p.ambiguous + p.unmatched;
  const few = m.matched < MIN_CHECK || p.matched < MIN_READERS;
  return (
    <div className="outreach-readers">
      {p.matched === 0 ? (
        <p className="outreach__muted">No visit by a person could be matched to its email{notMatched ? ` (${plural(notMatched, 'visit')} could not be matched to one recipient)` : ''}.</p>
      ) : (
        <div className="outreach__headline">
          <div>
            <span className="outreach__big outreach__big--hidden">{fmt(p.notBefore)} of {fmt(p.matched)}</span>
            <span className="outreach__big-label">visits by people came from an email with <strong>no open recorded before the click</strong><br />
              <small>{said([
                p.none > 0 && `${fmt(p.none)} had no open at all`,
                p.atClick > 0 && `${fmt(p.atClick)} ${p.atClick === 1 ? 'has' : 'have'} one only at the moment of the click, which HubSpot records when a link is clicked`,
                p.after > 0 && `${fmt(p.after)} only later`,
                p.machine > 0 && `${fmt(p.machine)} only a machine’s`,
              ])}</small>
            </span>
          </div>
          <div>
            <span className="outreach__big">{fmt(p.seen)} of {fmt(p.matched)}</span>
            <span className="outreach__big-label">had a picture loaded first<br />
              <small>{p.seen > 0 ? said([p.program > 0 && `${fmt(p.program)} by a mail program`, p.apple > 0 && `${fmt(p.apple)} by Apple’s service`, p.browser > 0 && `${fmt(p.browser)} by a browser name, which among machines is usually the scanner itself`]) : 'The open count had seen none of them before they clicked.'}</small>
            </span>
          </div>
        </div>
      )}

      <p className="outreach__plain outreach-readers__scope">
        <strong>The whole campaign, and {p.matched === 1 ? 'one visit' : `${fmt(p.matched)} visits`} by people: a count, not a percentage of readers.</strong> The figures above do not change with the waves or emails chosen: they are kept only as totals, so that none can be traced to a visit.
        {soon.of > 0 ? ` ${fmt(soon.n)} of the ${plural(soon.of, 'visit')} by people came within ten minutes of a send, when scanners’ clicks are densest and a match is least sure.` : ''}
      </p>

      <table className="outreach-readers__table">
        <caption>Each visit set beside the click that led to it: had that copy of the email loaded a picture before the click?</caption>
        <thead>
          <tr>
            <th scope="col">Visits</th>
            <th scope="col" className="num">A picture had loaded</th>
            <th scope="col" className="num">Open only at the click, or after</th>
            <th scope="col" className="num">No open, or only a machine’s</th>
            <th scope="col" className="num">Matched</th>
          </tr>
        </thead>
        <tbody>
          {rowsOf.map(([band, label]) => {
            const x = bands[band];
            return (
              <tr key={band} className={`outreach-readers__row outreach-readers__row--${band}`}>
                <th scope="row">
                  {label}
                  <CompositionBar
                    height={12} minShare={0} showLegend={false} unit="visits"
                    segments={Object.keys(READER_COLORS).map((k) => ({ key: k, label: READER_LABEL[k], value: x[k], color: READER_COLORS[k] }))}
                  />
                </th>
                <td className="num"><b>{fmt(x.seen)}</b>{x.seen > 0 && <small>{said([x.program > 0 && `${fmt(x.program)} a mail program`, x.apple > 0 && `${fmt(x.apple)} Apple’s service`, x.browser > 0 && `${fmt(x.browser)} a browser name`])}</small>}</td>
                <td className="num"><b>{fmt(x.atOrAfter)}</b>{x.atOrAfter > 0 && <small>{said([x.atClick > 0 && `${fmt(x.atClick)} at the click`, x.after > 0 && `${fmt(x.after)} later`])}</small>}</td>
                <td className="num"><b>{fmt(x.unseen)}</b>{x.unseen > 0 && <small>{said([x.none > 0 && `${fmt(x.none)} no open at all`, x.machine > 0 && `${fmt(x.machine)} only a machine’s`])}</small>}</td>
                <td className="num">{fmt(x.matched)}{x.ambiguous + x.unmatched > 0 && <small>{fmt(x.ambiguous + x.unmatched)} not matched</small>}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="outreach-readers__key" aria-hidden="true">
        {['program', 'apple', 'browser', 'atClick', 'after', 'machine', 'none'].map((k) => <span key={k}><i style={{ background: READER_COLORS[k] }} />{READER_LABEL[k]}</span>)}
      </div>

      <p className="outreach__plain outreach-readers__estimate">
        {statement.ok ? (
          <>
            <strong>About {inTen(statement.missed.rate)} in 10 of the readers we can prove had no picture loaded when they clicked</strong>: the open count has them only because they clicked.
            {' '}On so few it could be {inTen(statement.missed.low)} to {inTen(statement.missed.high)} in 10. It is a share of the readers who clicked, and says nothing of how many read and did not.
          </>
        ) : statement.why === 'few' ? (
          <><strong>A count, not yet a share.</strong> {plural(p.matched, 'visit')} by people {p.matched === 1 ? 'has' : 'have'} been matched to {p.matched === 1 ? 'its' : 'their'} email, and it takes {MIN_READERS} before a share of readers is drawn from them. Every wave adds to it.</>
        ) : statement.why === 'no-check' ? (
          <><strong>A count, not yet a share:</strong> only {plural(m.matched, 'machine visit')} could be matched, and the check needs {MIN_CHECK}.</>
        ) : statement.why === 'check-high' ? (
          <><strong>A count, not a share: the check did not pass.</strong> A person’s mail program appears to have loaded a picture before {formatRate(readers.control.rate)} of the machines’ clicks, and that should be close to never (at most {formatRate(CHECK_CEILING)}). The kinds of visitor, or the matching, are not to be trusted on these figures.</>
        ) : (
          <>
            <strong>A count, not a share: the check did not pass.</strong> The check asks one thing: is “a person’s own mail program had loaded a picture before the click” clearly more common for people than for machines? Here it is {fmt(p.program)} of {fmt(p.matched)} against {fmt(m.program)} of {fmt(m.matched)}: too close to call.
            {' '}That is about this one measure. It is no doubt that the {p.matched === 1 ? 'visit was a person' : `${fmt(p.matched)} visits were people`}: they are counted as people for what they did on the site. So the count stands, and no share of all readers is drawn from it.
          </>
        )}
      </p>
      <ul className="outreach__points">
        <li>
          <strong>The check.</strong> A scanner clicks as the email arrives, before anyone could have read it.
          {' '}{m.matched === 0 ? 'No machine visit could be matched.' : (
            <>
              A person’s own mail program had loaded a picture before {fmt(m.program)} of {fmt(m.matched)} machine visits ({formatRate(readers.control.rate)})
              {p.matched > 0 ? `, against ${fmt(p.program)} of ${fmt(p.matched)} visits by people (${formatRate(readers.people.rate)}).` : '.'}
            </>
          )}
          {' '}{few ? 'Too few to judge yet.' : statement.ok ? 'The two are clearly apart.' : statement.why === 'check-high' ? 'For machines that is too high to trust.' : 'On this measure the two are not clearly apart.'}
        </li>
        {m.browser > 0 && (
          <li>
            <strong>An open HubSpot kept is not always a person.</strong> Before {fmt(m.browser)} of the {fmt(m.matched)} machine visits, something calling itself a browser had loaded a picture, and HubSpot had counted it as an open.
            {' '}So “a picture had loaded” does not prove a person had pictures on, and the open count is padded as well as short. That is why the estimate above starts from mail-program opens only.
          </li>
        )}
        <li>
          <strong>What nothing can count.</strong> A reader who hides pictures and does not click leaves no trace with HubSpot, with us, or with anyone.
        </li>
      </ul>
      {recipients && <Wider wider={recipients.wider} hubspotUnsubscribed={recipients.hubspotUnsubscribed} proven={p.matched} />}

      <p className="outreach__note">
        {notMatched > 0 ? `${plural(notMatched, 'visit')} by people could not be matched to one recipient (${fmt(p.unmatched)} with no click of that email near the visit, ${fmt(p.ambiguous)} where more than one recipient fits) and ${notMatched === 1 ? 'is' : 'are'} left out. ` : ''}
        The match is made while the figures are built and is not kept: this page holds totals, not which visit was whose.{' '}
        <button type="button" className="outreach__link" onClick={onGuide}>How it is measured</button>
      </p>
    </div>
  );
};
Readers.propTypes = { readers: PropTypes.object, recipients: PropTypes.object, onGuide: PropTypes.func.isRequired };

/* ── a wider base: every click HubSpot kept, and everyone who unsubscribed ─────────────── */
const Wider = ({ wider, hubspotUnsubscribed, proven }) => {
  // A share is given only on a base that can bear one, and then with its range: under a
  // filter a group can be two or three emails, and "67%" of three is no measurement.
  const share = (k, n) => { const w = n >= SMALL_BASE ? wilson(k, n) : null; return w ? ` · ${formatRate(w.rate)} (could be ${formatRate(w.low)} to ${formatRate(w.high)})` : ''; };
  const rows = [
    { key: 'clicked', label: 'Clicked, as HubSpot judges a person', sub: 'every click it kept, with or without a visit of ours', x: wider.clicked, click: true },
    wider.link && { key: 'link', label: 'Unsubscribed by the link in the email', sub: 'so had the email open', x: wider.link },
    wider.button && { key: 'button', label: 'Unsubscribed with their mail program’s button', sub: 'can be done without opening the email', x: wider.button },
  ].filter(Boolean);
  const read = wider.unsubsRead;
  const unsubs = read ? wider.link.total + wider.button.total + wider.other.total : 0;
  return (
    <div className="outreach-wider">
      <h4 className="outreach__subhead">A wider base: everyone who did something that needs no picture</h4>
      <p className="outreach__note outreach-wider__lead">
        {proven === 1 ? 'One visit is' : `${fmt(proven)} visits are`} everyone we can prove clicked through. These groups are larger, on HubSpot’s word alone that they were people, and they <strong>do follow the waves and emails chosen above</strong>.
      </p>
      <table className="outreach-readers__table outreach-wider__table">
        <caption>Had any open been recorded before they did it?</caption>
        <thead>
          <tr>
            <th scope="col">Emails</th>
            <th scope="col" className="num">An open before</th>
            <th scope="col" className="num">No open before</th>
            <th scope="col" className="num">In all</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ key, label, sub, x, click }) => (
            <tr key={key}>
              <th scope="row">{label}<small>{sub}</small></th>
              <td className="num">{fmt(x.openBefore)}</td>
              <td className="num">
                <b>{fmt(x.notBefore)}</b>
                {x.total > 0 && <small>of {fmt(x.total)}{share(x.notBefore, x.total)}</small>}
                {x.notBefore > 0 && <small>{said([click && x.atMoment > 0 && `${fmt(x.atMoment)} only at the click`, x.later > 0 && `${fmt(x.later)} only later`, x.none > 0 && `${fmt(x.none)} none, or only a machine’s`])}</small>}
              </td>
              <td className="num">{fmt(x.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="outreach__note">
        {!read ? (
          <>Who unsubscribed was <strong>not read</strong> on these figures{hubspotUnsubscribed ? ` (HubSpot counts ${fmt(hubspotUnsubscribed)} for these emails)` : ''}: it is not shown, and that is not “nobody”.</>
        ) : unsubs === 0 && hubspotUnsubscribed > 0 ? (
          <>HubSpot counts {fmt(hubspotUnsubscribed)} unsubscribed for these emails, but gave no record of them one by one: <strong>not measured</strong>, which is not “nobody”.</>
        ) : (
          <>
            {plural(unsubs, 'unsubscribe')} read{hubspotUnsubscribed != null ? `; HubSpot’s own count for these emails is ${fmt(hubspotUnsubscribed)}` : ''}.
            {wider.other.total > 0 ? ` ${fmt(wider.other.total)} came about some other way (one of us changed it, or a bounce) and ${wider.other.total === 1 ? 'is' : 'are'} left out.` : ''}
          </>
        )}
        {' '}“Before” is judged as it must be for each: an open only at the moment of a click is most likely the click itself, so it is not before; an unsubscribe makes no open of its own, so an open at that moment is.
      </p>
    </div>
  );
};
Wider.propTypes = { wider: PropTypes.object.isRequired, hubspotUnsubscribed: PropTypes.number, proven: PropTypes.number.isRequired };

/* ── what happened to each email delivered (each recipient's own events) ─────────────────── */
const Deliveries = ({ recipients }) => {
  const s = recipients.sends;
  const keptOpen = s.fateRead + s.fateSkimmed + s.fateGlanced + s.fateUntimed;
  return (
    <>
      <CompositionBar height={18} minShare={0} unit="emails" segments={FATE_SEGMENTS.map((f) => ({ ...f, value: s[f.key] }))} />
      <ul className="outreach__points">
        <li>
          <strong>What HubSpot threw out.</strong> An open of some kind was recorded for {fmt(s.openedAny)} of the {fmt(s.delivered)} emails delivered.
          {' '}For {fmt(s.machineOnly)} of them every open was one HubSpot marked as a machine; {fmt(keptOpen)} are left.
          {' '}A click was recorded for {fmt(s.clickedAny)}, and {fmt(s.clicked)} of those it took for a person.
        </li>
        <li>
          <strong>Behind a scanner:</strong> {fmt(s.scanned)} of the emails ({s.delivered ? formatRate(s.scanned / s.delivered) : '—'}) went to an address where something opens or clicks automatically. That is why raw opens and clicks cannot be read as people.
        </li>
        <li>
          <strong>Came back to it:</strong> {fmt(s.openedAgain)} of the {fmt(keptOpen)} opened the same email again half an hour or more later.
        </li>
      </ul>
      <p className="outreach__note">
        From each recipient’s own events ({fmt(recipients.events.delivered)} deliveries, {fmt(recipients.events.opens)} opens and {fmt(recipients.events.clicks)} clicks read), counted by the contact’s own wave and role, so emails 2 and 3 divide by heads and others here.
        {recipients.notPlaced > 0 ? ` ${plural(recipients.notPlaced, 'email')} to people who are no longer contacts cannot be placed by this choice and ${recipients.notPlaced === 1 ? 'is' : 'are'} left out.` : ''}
        {recipients.cut.length > 0 ? ' Some emails had more events than a build reads: for those these are a floor.' : ''}
      </p>
    </>
  );
};
Deliveries.propTypes = { recipients: PropTypes.object.isRequired };

/* ── every email ─────────────────────────────────────────────────────────────────────────── */
const ROLE_WORD = { head: 'Heads', other: 'Others', both: 'Both' };
/** Do the recipients' events add up to something other than HubSpot's own figure for the email? */
const differs = (e, key) => Boolean(e.events) && e[key] != null && e.events[key] !== e[key];
const EmailTable = ({ rows, other, estimateFor }) => (
  <div className="outreach__scroll">
    <table className="outreach-table">
      <thead>
        <tr>
          {!other && <th scope="col">Wave</th>}
          {!other && <th scope="col">Email</th>}
          {!other && <th scope="col">To</th>}
          <th scope="col">Sent</th>
          <th scope="col" className="outreach-table__title">Title and subject</th>
          <th scope="col" className="num">Delivered</th>
          <th scope="col" className="num" title="As HubSpot records it: a picture was fetched, or a link clicked">Opened</th>
          {!other && <th scope="col" className="num" title="Kept open for 8 seconds or more, from each recipient’s own events">Kept open 8 s+</th>}
          <th scope="col" className="num" title="As HubSpot records it">Clicked</th>
          {!other && <th scope="col" className="num" title="Replies HubSpot recorded">Replied</th>}
          {!other && estimateFor && <th scope="col" className="num outreach-est__cell" title="An estimate, not a count: emails probably opened by a person. The range is what to read.">Probably opened <small>estimate</small></th>}
          {!other && <th scope="col" className="num" title="Visits to our site by people on the evidence · that we cannot tell · by machines">Visits: people · cannot tell · machines</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((e) => (
          <tr key={e.key} className={e.placed || other ? '' : 'outreach-table__row--unplaced'}>
            {!other && <td className="outreach-table__when">{e.waves && e.waves.length ? e.waves.join(' & ') : '—'}</td>}
            {!other && <td>{e.step == null ? '—' : e.step}</td>}
            {!other && <td>{ROLE_WORD[e.role] || '—'}</td>}
            <td className="outreach-table__when">{dateTime(e.sentAt)}{e.counting && <span className="outreach__badge" title="Under a week old: still collecting opens and clicks">still counting</span>}</td>
            <td className="outreach-table__title">
              <strong>{e.name}</strong>
              {e.subject && <small>{e.subject}</small>}
              {e.disagree && <small className="outreach-table__warn">{e.disagree}</small>}
              {!other && !e.placed && <small className="outreach-table__warn">Not placed in the series: its title is not in a form the report knows.</small>}
            </td>
            <td className="num">{fmt(e.delivered)}{differs(e, 'delivered') && <small className="outreach-table__events">events: {fmt(e.events.delivered)}</small>}</td>
            <td className="num">{e.opened == null ? '—' : <>{fmt(e.opened)} <small>{e.delivered ? formatRate(e.opened / e.delivered) : ''}</small></>}{differs(e, 'opened') && <small className="outreach-table__events">events: {fmt(e.events.opened)}</small>}</td>
            {!other && <td className="num">{e.events ? <>{fmt(e.events.read)} <small>{e.events.delivered ? formatRate(e.events.read / e.events.delivered) : ''}</small></> : '—'}</td>}
            <td className="num">{e.clicked == null ? '—' : <>{fmt(e.clicked)} <small>{e.delivered ? formatRate(e.clicked / e.delivered) : ''}</small></>}{differs(e, 'clicked') && <small className="outreach-table__events">events: {fmt(e.events.clicked)}</small>}</td>
            {!other && <td className="num">{e.replied == null ? '—' : fmt(e.replied)}</td>}
            {!other && estimateFor && (() => {
              const x = estimateFor(e);
              return (
                <td className={`num outreach-est__cell${x && x.ok ? ` outreach-est--${x.confidence}` : ''}`}>
                  {x && x.ok ? <><span className="outreach-est__figure">{estimateText(x.share)}</span>{estimateMiddle(x.share) && <small>about {estimateMiddle(x.share)}</small>}</>
                    : x && x.why === 'thin-count' ? <small>too few counted opens</small> : '—'}
                </td>
              );
            })()}
            {!other && <td className="num outreach-table__visits">{e.visits ? <><b>{fmt(e.visits.person)}</b> · {fmt(e.visits.unsure)} · <span className="outreach__muted">{fmt(e.visits.machine)}</span></> : '—'}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
EmailTable.propTypes = { rows: PropTypes.array.isRequired, other: PropTypes.bool, estimateFor: PropTypes.func };

/* ── the report ──────────────────────────────────────────────────────────────────────────── */
const OutreachReport = ({
  report, loading = false, error = null, color, isAdmin = false, onRefresh, refreshing = false, refreshError = null,
  filters: controlled, onFiltersChange, now,
}) => {
  const [stored, setStored] = usePersistedState('outreach.filters', DEFAULT_FILTERS);
  const filters = normaliseFilters(controlled || stored);
  const setFilters = onFiltersChange || setStored;
  const [metric, setMetric] = usePersistedState('outreach.metric', 'opened');
  const [guideOpen, setGuideOpen] = usePersistedState('outreach.guide', false);
  const [showOther, setShowOther] = useState(false);

  // Whatever was stored, made safe to draw; null when it cannot be read (an older shape, an
  // empty or hand-written document). The page then says so: it never throws on a missing part.
  const storedCube = report ? report.cube : null;
  const cube = useMemo(() => usableCube(storedCube), [storedCube]);
  const status = (report && report.status) || {};
  const sel = useMemo(() => (cube ? select(cube, filters) : null), [cube, filters.waves.join(','), filters.role, filters.step]); // eslint-disable-line react-hooks/exhaustive-deps
  // Without HubSpot's per-email figures only the per-person measure can be compared.
  const emailFigures = hasEmailFigures(cube);
  const metricKey = !emailFigures ? 'peopleOpened' : metricsFor(cube).includes(metric) ? metric : 'opened';
  const comparisons = useMemo(() => (cube ? {
    wave: compare(cube, filters, 'wave', metricKey),
    role: compare(cube, filters, 'role', metricKey),
    step: compare(cube, filters, 'step', metricKey),
  } : null), [cube, filters.waves.join(','), filters.role, filters.step, metricKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error && !cube) return <InfoNotice variant="error">Could not load the outreach figures: {error}</InfoNotice>;
  if (loading && !cube) return <LoadingState message="Reading the outreach figures…" minHeight={320} />;
  if (!cube && storedCube != null) {
    return (
      <EmptyState
        title="The stored figures could not be read"
        description="They were made by a different version of this report, or the stored record is damaged. A refresh builds them again."
        action={isAdmin && onRefresh ? <Button onClick={onRefresh} loading={refreshing} disabled={refreshing || status.running}>Build them again</Button> : undefined}
      />
    );
  }
  if (!cube) {
    return (
      <EmptyState
        title="No outreach figures yet"
        description={status.running ? 'The first figures are being built now. It takes two or three minutes.' : 'They are built each night at 04:40 from HubSpot and our own records.'}
        action={isAdmin && onRefresh ? <Button onClick={onRefresh} loading={refreshing} disabled={refreshing || status.running}>Build them now</Button> : undefined}
      />
    );
  }

  const fresh = freshness(status, now);
  const pictures = picturesOf(cube);
  const arrival = arrivalOf(cube, filters);
  const estimate = estimatesOf(cube, filters);
  const floor = floorOf(cube, filters);
  const estimateByGroup = { wave: estimateRows(cube, filters, 'wave'), role: estimateRows(cube, filters, 'role'), step: estimateRows(cube, filters, 'step') };
  // each email's own estimate, for the table: its wave(s), its place in the series, who it went to
  const estimateFor = (e) => (e.placed ? estimatesOf(cube, { waves: e.waves, step: e.step, role: e.role === 'head' || e.role === 'other' ? e.role : 'all' }) : null);
  const series = acrossSeries(sel);
  const v = sel.visits;
  const basis = basisOf(sel);
  const openRate = basis.base ? wilson(basis.opened || 0, basis.base) : null;
  const chosen = [
    filters.waves.length ? `wave${filters.waves.length > 1 ? 's' : ''} ${filters.waves.join(', ')}` : 'every wave sent',
    filters.role === 'all' ? null : filters.role === 'head' ? 'heads only' : 'others only',
    filters.step === 'all' ? null : `email ${filters.step} only`,
  ].filter(Boolean).join(', ');
  const others = emailRows(cube, { otherSends: true });
  const tableRows = emailRows(cube).filter((e) => emailShown(e, filters));
  const rec = sel.recipients;
  const metricOptions = metricsFor(cube);
  const pictureDays = pictures.days.filter((d) => d.program + d.apple + d.scanner > 0);

  return (
    <div className={`outreach${guideOpen ? ' outreach--guide' : ''}`} style={color ? { '--outreach-accent': color } : undefined}>
      <div className="outreach__main">
        <div className="outreach__top">
          <p className={`outreach__fresh${fresh.stale ? ' outreach__fresh--stale' : ''}`}>
            {fresh.text}{status.builtAt ? ` (${dateTime(status.builtAt)})` : ''}
          </p>
          <div className="outreach__actions">
            {isAdmin && onRefresh && (
              <Button variant="secondary" size="small" icon={<FaSyncAlt />} onClick={onRefresh} loading={refreshing} disabled={refreshing || status.running}
                title="Read HubSpot and our records again now. Takes two or three minutes.">
                {refreshing ? 'Refreshing…' : 'Refresh now'}
              </Button>
            )}
            <Button variant={guideOpen ? 'primary' : 'secondary'} size="small" icon={<FaBookOpen />} aria-expanded={guideOpen} aria-controls="outreach-guide"
              onClick={() => setGuideOpen(!guideOpen)}>
              How to read this
            </Button>
          </div>
        </div>
        {refreshError && <InfoNotice variant="warning">{refreshError}</InfoNotice>}
        {status.lastFailed && !refreshError && (
          <InfoNotice variant="warning">The last refresh failed, so these are the figures from before it.{isAdmin && status.lastError ? ` (${status.lastError})` : ''}</InfoNotice>
        )}
        {cube.notRead && cube.notRead.length > 0 && (
          <InfoNotice variant="warning">
            Not read on the last build: {cube.notRead.map((n) => `${n.what} (${n.why})`).join('; ')}. The rest of the page is whole.
          </InfoNotice>
        )}

        <Filters cube={cube} filters={filters} onChange={setFilters} />

        {sel.unsplit && (
          <InfoNotice variant="info">
            {!rec && sel.left.emailsByRole + sel.left.visitsByRole > 0 && (
              <>
                Emails 2 and 3 go to heads and others together, so they cannot be split by role.
                {' '}{plural(sel.left.emailsByRole, 'email')} and {plural(sel.left.visitsByRole, 'visit')} are left out of the figures below
                {sel.sent.steps.length ? `, which are for email ${sel.sent.steps.join(' and ')}.` : '.'}{' '}
              </>
            )}
            {rec && sel.left.visitsByRole > 0 && (
              <>
                Emails 2 and 3 go to heads and others together, and a visit does not say which its visitor is:
                {' '}{plural(sel.left.visitsByRole, 'visit')} {sel.left.visitsByRole === 1 ? 'is' : 'are'} left out of the figures below.
                {' '}Opens and clicks are divided, from each recipient’s own events.{' '}
              </>
            )}
            {!rec && sel.left.emailsByWave + sel.left.visitsByWave > 0 && (
              <>
                One email went to two waves together, and only one of them is chosen.
                {' '}{plural(sel.left.emailsByWave, 'email')} and {plural(sel.left.visitsByWave, 'visit')} from it cannot be divided between them, and are left out.
              </>
            )}
            {rec && sel.left.visitsByWave > 0 && (
              <>
                One email went to two waves together, and only one of them is chosen.
                {' '}{plural(sel.left.visitsByWave, 'visit')} from it cannot be divided between them, and {sel.left.visitsByWave === 1 ? 'is' : 'are'} left out.
                {' '}Its opens and clicks are divided, by each contact’s own wave.
              </>
            )}
          </InfoNotice>
        )}

        {/* 1. The headline */}
        <Block title="From the inbox to the website" subtitle={`For ${chosen}: ${plural(sel.sent.touching, 'email')}${basis.kind === 'people' ? ` to ${fmt(basis.base)} people` : ''}.`}>
          <Ladder rungs={ladder(sel)} />
          <p className="outreach__plain">
            <strong>“Cannot tell” is not “probably not a person”.</strong>{' '}
            {fmt(v.person.n)} {v.person.n === 1 ? 'visit was' : 'visits were'} by people on the evidence (they did something a link scanner does not);
            {' '}{fmt(v.unsure.n)} more could be either: one page, from a UK network, which is what a solicitor glancing at the site looks like and what some scanners look like.
            {' '}The response is somewhere between the two. {fmt(v.machine.n)} {v.machine.n === 1 ? 'was a machine' : 'were machines'}.
          </p>
        </Block>

        {/* 2. Are the emails arriving at all? */}
        <Block
          title="Are the emails arriving?"
          subtitle="A handful of proven visits out of thousands sent is a measure of who clicked, not of what arrived. This is everything that bears on arrival."
        >
          <Arrival
            arrival={arrival} proven={v.person.n} programs={pictures.totals.program}
            topProgram={(() => { const top = Object.entries(pictures.programs).sort((a, b) => b[1] - a[1])[0]; return top ? PROGRAM_LABEL[top[0]] || null : null; })()}
          />
        </Block>

        {/* 3. How many probably read it: the estimate, marked as one */}
        <Block
          title="How many probably read it"
          subtitle="HubSpot’s open count misses readers with pictures off and counts opens that were nobody. This corrects for both, as far as the evidence in the next panel allows."
        >
          <Estimate est={estimate} rows={estimateByGroup} onGuide={() => setGuideOpen(true)} />
        </Block>

        {/* 3b. At least, on the record: the count that sits under the estimate */}
        <Block
          title="At least, on the record"
          subtitle="Emails that were in front of a person, counted from each recipient’s own events. The estimate above is what probably happened; this is the least that did."
        >
          <Floor floor={floor} />
        </Block>

        {/* 4. The readers an open count cannot see: people reading with pictures off */}
        <Block
          title="Readers the open count cannot see"
          subtitle="An open is recorded only when a picture loads, and Outlook hides pictures from a sender it does not know. A click needs no picture. So each visit to our site is set beside its own email: had a picture loaded when they clicked? For the whole campaign."
        >
          <Readers readers={sel.readers} recipients={rec} onGuide={() => setGuideOpen(true)} />
        </Block>

        {/* 3. What happened to each email, from each recipient's own events */}
        {rec && (
          <Block title="What happened to each email delivered" subtitle={`${fmt(rec.sends.delivered)} emails delivered for this choice, each counted once by the most it shows.`}>
            <Deliveries recipients={rec} />
          </Block>
        )}

        {/* 4. Opens */}
        <div className="outreach__grid">
          <Block title="What “opened” is worth" subtitle="HubSpot counts an open when a picture in the email is fetched. This is our own count of what did the fetching.">
            <div className="outreach__headline">
              <div>
                <span className="outreach__big">{openRate ? formatRate(openRate.rate) : '—'}</span>
                <span className="outreach__big-label">{basis.kind === 'people' ? 'of people opened at least one email, as HubSpot records it' : 'opened, as HubSpot records it'}<br /><small>{fmt(basis.opened)} of {fmt(basis.base)} {basis.ofLabel}{basis.counting ? ' · still counting' : ''}</small></span>
              </div>
            </div>
            <CompositionBar
              height={16}
              minShare={0}
              unit="fetches"
              segments={[
                { key: 'program', label: 'A person’s own mail program', value: pictures.totals.program, color: BAND_COLORS.person, help: 'Outlook, a phone’s mail app, webmail in a real browser. The only kind that means the email was on a screen.' },
                { key: 'apple', label: 'Apple’s privacy service', value: pictures.totals.apple, color: '#7c93b3', help: 'Fetches every picture for Apple Mail users, read or not.' },
                { key: 'scanner', label: 'A security scanner, as the email arrived', value: pictures.totals.scanner, color: BAND_COLORS.machine, help: 'Fetches every picture to check it. Nobody was looking.' },
              ]}
            />
            <ul className="outreach__points">
              <li><strong>It misses people.</strong> Anyone reading with pictures off is not counted, and desktop Outlook hides pictures from a sender it does not know. How many is measured in the panel above.</li>
              <li><strong>It counts machines.</strong> {pictures.total ? formatRate(pictures.totals.scanner / pictures.total) : '—'} of the fetches we saw were a scanner. HubSpot filters only the ones it recognises.</li>
              <li><strong>It is still fair for comparing</strong> one wave or email with another: the same faults apply to each.</li>
            </ul>
            <p className="outreach__note">
              All our emails share one logo, so this count is for every HubSpot email since {pictures.since ? dayLabel(pictures.since) : 'the first send'}, not only the waves chosen above.
              {' '}{fmt(pictures.totals.preview)} fetches by us (previews and tests) are left out.{' '}
              <button type="button" className="outreach__link" onClick={() => setGuideOpen(true)}>How far “opened” can be trusted</button>
            </p>
          </Block>

          <Block title="Picture fetches, day by day" subtitle="The scanner’s fetches arrive with each send. People’s follow over the hours and days after.">
            <SeriesChart
              variant="stacked"
              height={230}
              unit="fetches"
              labels={pictureDays.map((d) => dayLabel(d.day))}
              series={[
                { key: 'program', label: 'People’s mail programs', color: '#15803d', values: pictureDays.map((d) => d.program) },
                { key: 'apple', label: 'Apple’s privacy service', color: '#7c93b3', values: pictureDays.map((d) => d.apple) },
                { key: 'scanner', label: 'Security scanners', color: '#cbd5e1', values: pictureDays.map((d) => d.scanner) },
              ]}
              emptyText="No picture fetches recorded yet."
            />
            <div className="outreach__programs">
              <h4 className="outreach__subhead">Which mail programs</h4>
              <RankedList
                labelHeader="Program" valueHeader="Fetches" total={pictures.totals.program} color="#15803d"
                rows={Object.entries(pictures.programs).sort((a, b) => b[1] - a[1]).map(([k, n]) => ({ key: k, label: PROGRAM_LABEL[k] || k, value: n }))}
                emptyText="None yet."
              />
            </div>
          </Block>
        </div>

        {/* 5. Visits */}
        <div className="outreach__grid">
          <Block title="Who clicked: people, machines, cannot tell" subtitle={`${fmt(v.total)} visits reached our site from these emails. Each is put in a band by the first rule that applies.`}>
            <CompositionBar
              height={16}
              minShare={0}
              unit="visits"
              segments={[
                { key: 'person', label: 'People, on the evidence', value: v.person.n, color: BAND_COLORS.person, help: 'Accepted cookies, opened a second page, used the slider, clicked a button, played the video or started a quote.' },
                { key: 'unsure', label: 'Cannot tell', value: v.unsure.n, color: BAND_COLORS.unsure, help: 'No sign of a machine, and nothing only a person does. Not "probably not a person".' },
                { key: 'machine', label: 'Machines', value: v.machine.n, color: BAND_COLORS.machine, help: 'See the reasons below.' },
              ]}
            />
            <h4 className="outreach__subhead">Why a visit was counted as a machine</h4>
            <RankedList
              labelHeader="Reason" valueHeader="Visits" total={v.machine.n} color="#94a3b8"
              rows={Object.keys(KIND_LABEL).map((k) => ({ key: k, label: KIND_LABEL[k][0], sub: KIND_LABEL[k][1], value: v.kinds[k] })).sort((a, b) => b.value - a.value)}
              emptyText="None."
            />
            <p className="outreach__note">
              Time on the page is not used: the visits known to be machines cluster at 19 to 25 seconds, so a scanner can “read” for longer than a person.
              {v.team ? ` ${plural(v.team, 'visit')} by us ${v.team === 1 ? 'is' : 'are'} left out.` : ''}
              {cube.noEmailId && cube.noEmailId.visits ? ` ${plural(cube.noEmailId.visits, 'visit')} with no email id (test emails and previews) ${cube.noEmailId.visits === 1 ? 'is' : 'are'} left out.` : ''}
            </p>
          </Block>

          <Block title="When they arrived, and by which link" subtitle="Machines arrive with the email. So do some people: arriving early is not, on its own, a sign of a machine.">
            <Timing timing={sel.timing} />
            <h4 className="outreach__subhead">By link</h4>
            <table className="outreach-links">
              <thead><tr><th scope="col">Link in the email</th><th scope="col" className="num">People</th><th scope="col" className="num">Cannot tell</th><th scope="col" className="num">Machines</th></tr></thead>
              <tbody>
                <tr><th scope="row">go.clozesure.com link <small>lands on the conveyancers’ page · email 1 only</small></th><td className="num"><b>{fmt(v.byLink.go.person)}</b></td><td className="num">{fmt(v.byLink.go.unsure)}</td><td className="num outreach__muted">{fmt(v.byLink.go.machine)}</td></tr>
                <tr><th scope="row">clozesure.com link <small>lands on the home page · every email</small></th><td className="num"><b>{fmt(v.byLink.direct.person)}</b></td><td className="num">{fmt(v.byLink.direct.unsure)}</td><td className="num outreach__muted">{fmt(v.byLink.direct.machine)}</td></tr>
              </tbody>
            </table>
            {sel.goHits.n > 0 && (
              <p className="outreach__note">
                The go link itself was hit {fmt(sel.goHits.n)} times; {fmt(v.byLink.go.person + v.byLink.go.unsure + v.byLink.go.machine)} of those went on to load the site. The rest fetched the link and never opened the page: scanners.
              </p>
            )}
          </Block>
        </div>

        {/* 6. What the people did */}
        <div className="outreach__grid">
          <Block title="What the people did" subtitle="Only the visits by people on the evidence. Reading time means something here, because these are known not to be scanners.">
            {v.person.n === 0 ? <p className="outreach__muted">No visit by a person, on the evidence, for this choice.</p> : (
              <RankedList
                labelHeader="Of the visits by people" valueHeader="Visits" total={v.person.n} color="#15803d"
                rows={[
                  { key: 'cookies', label: 'Accepted cookies', value: v.person.cookies },
                  { key: 'read15', label: 'Stayed 15 seconds or more', value: v.person.read15 },
                  { key: 'read60', label: 'Stayed a minute or more', value: v.person.read60 },
                  { key: 'second', label: 'Opened a second page', value: v.person.secondPage },
                  { key: 'slider', label: 'Used the price slider', value: v.person.slider },
                  { key: 'video', label: 'Played the video', value: v.person.video },
                  { key: 'cta', label: 'Clicked a button', value: v.person.cta },
                  { key: 'quote', label: 'Ran a quote', value: v.person.quote },
                ]}
              />
            )}
            <p className="outreach__note">
              Coming back: {plural(cube.returning.recognised, 'visitor')} accepted cookies and so could be recognised;
              {' '}{fmt(cube.returning.cameBack)} came back later, {fmt(cube.returning.onALaterEmail)} of them from a later email. A floor, and for every wave together.
            </p>
          </Block>
          <Block title="Pages they saw" subtitle="Each page once per visit. People on the evidence, with the visits we cannot tell beside them.">
            <RankedList
              labelHeader="Page" valueHeader="People" color="#15803d" limit={8}
              columns={[{ key: 'unsure', label: 'Cannot tell', align: 'right', width: '84px' }, { key: 'landed', label: 'Landed here', align: 'right', width: '84px' }]}
              rows={sel.pages.map((p) => ({ key: p.page, label: p.page, value: p.person, cells: { unsure: p.unsure ? fmt(p.unsure) : '–', landed: p.landed ? fmt(p.landed) : '–' } }))}
              emptyText="No pages seen for this choice."
            />
          </Block>
        </div>

        {/* 7. Comparisons */}
        <Block
          title="Does one do better than another?"
          subtitle="Each dot is a rate; the pale band is the range it could lie in by chance alone. Two groups differ only where their bands do not overlap."
          actions={(
            <SegmentedControl
              value={metricKey}
              onChange={setMetric}
              options={metricOptions.map((k) => ({ value: k, label: METRICS[k].label, title: `${METRICS[k].long}, as a share of ${METRICS[k].of}` }))}
            />
          )}
        >
          <p className="outreach__measuring">
            Measuring: <strong>{METRICS[metricKey].long}</strong>, as a share of {METRICS[metricKey].of}.
            {!emailFigures && ' HubSpot’s figures for each email were not read, so opens and clicks per email cannot be compared.'}
          </p>
          <div className="outreach__grid outreach__grid--3">
            <Comparison title="Wave against wave" result={comparisons.wave} color={color} />
            <Comparison title="Heads against others" result={comparisons.role} color={color} />
            <Comparison title="First, second, third email" result={comparisons.step} color={color} />
          </div>
        </Block>

        {/* 8. Across the series */}
        <div className="outreach__grid">
          <Block
            title="Do the same people open every time?"
            subtitle={series.exact
              ? `Each person’s opens of each email, from their own events${rec.byPerson ? ' (for all the emails together, not the one chosen above)' : ''}.`
              : `HubSpot’s record of each contact, for all the emails together${sel.people.byEmail ? ' (not split by the email chosen above)' : ''}.`}
          >
            <div className="outreach__headline">
              <div>
                <span className="outreach__big">{fmt(series.openers)}</span>
                <span className="outreach__big-label">people opened at least one<br /><small>{series.base ? formatRate(series.openers / series.base) : '—'} of the {fmt(series.base)} {series.exact ? 'reached' : 'sent to'}</small></span>
              </div>
              <div>
                <span className="outreach__big">{fmt(series.again)}</span>
                <span className="outreach__big-label">opened more than one email<br /><small>{series.openers ? formatRate(series.again / series.openers) : '—'} of those who opened</small></span>
              </div>
            </div>
            <CompositionBar
              height={14}
              minShare={0}
              unit="people"
              segments={series.howMany.map((h, i) => ({
                key: h.key, label: h.label, value: h.value, color: h.key === 'mixed' ? '#cbd5e1' : ['#86b79a', '#3f9463', '#15803d'][i],
                help: h.key === 'mixed' ? 'Their count includes emails from before the outreach, so it cannot be read as "of these emails".' : undefined,
              }))}
            />
            {series.patterns.length > 0 && (
              <>
                <h4 className="outreach__subhead">Which of the emails</h4>
                <RankedList
                  labelHeader="Opened" valueHeader="People" color="#15803d" total={series.openers} limit={7}
                  rows={series.patterns.map((x) => ({ key: x.pattern, label: patternLabel(x.pattern), value: x.n }))}
                />
              </>
            )}
          </Block>
          <Block title="Which email first got the open?" subtitle={series.exact ? 'Of the people who opened: the first email of the series they opened. Does a follow-up reach new people?' : 'Of the people who opened: the email that had most recently gone out when they first did. Does a follow-up reach new people?'}>
            <RankedList
              labelHeader={series.exact ? 'First opened' : 'First opened after'} valueHeader="People" color={color}
              total={series.firstOpen.reduce((a, x) => a + x.n, 0)}
              rows={series.firstOpen.map((x) => ({ key: String(x.step), label: `Email ${x.step}`, value: x.n }))}
              emptyText="No opens yet for this choice."
            />
            {!series.exact && (
              <p className="outreach__note">
                Approximate: someone can open email 1 after email 2 has gone, and would be counted under email 2. HubSpot keeps only each contact’s first and latest open.
              </p>
            )}
          </Block>
        </div>

        {/* 9. When */}
        <Block title="When do they open?" subtitle="Fetches of the picture by people’s own mail programs, by weekday and hour in London. Scanners and Apple’s service are left out.">
          <HeatmapGrid matrix={pictures.hours} color="#15803d" unit="fetches" />
          <p className="outreach__note">Shaped by when we send as well as when they read: nearly every send so far went out mid-morning or at lunchtime.</p>
        </Block>

        {/* 10. Every email */}
        <Block
          title="Every email"
          subtitle="HubSpot’s own figures for each, and the visits that came from it. Where each recipient’s events add up to something else, that is shown beneath."
          actions={others.length > 0 ? (
            <Button variant="secondary" size="small" aria-pressed={showOther} onClick={() => setShowOther(!showOther)}>
              {showOther ? 'Back to the outreach waves' : `Other sends (${others.length})`}
            </Button>
          ) : undefined}
        >
          {showOther ? (
            <>
              <p className="outreach__note" style={{ marginTop: 0 }}>Sends that are not one of the waves. Shown for reference; they are in none of the figures above.</p>
              <EmailTable rows={others} other />
            </>
          ) : <EmailTable rows={tableRows} estimateFor={estimate && estimate.ok ? estimateFor : undefined} />}
        </Block>

        {cube.notes && cube.notes.length > 0 && (
          <ul className="outreach__notes">{cube.notes.map((n) => <li key={n}>{n}</li>)}</ul>
        )}
      </div>

      <aside className="outreach__guide" id="outreach-guide" aria-label="How to read this" hidden={!guideOpen}>
        <div className="outreach__guide-head">
          <h3>How to read this</h3>
          <button type="button" className="outreach__guide-close" onClick={() => setGuideOpen(false)} aria-label="Close the guide"><FaTimes /></button>
        </div>
        {guideOpen && <OutreachGuide sel={sel} pictures={pictures} check={cube.pictureCheck} cube={cube} />}
      </aside>
    </div>
  );
};

OutreachReport.propTypes = {
  /** { cube, status } from getOutreachReport. */
  report: PropTypes.shape({ cube: PropTypes.object, status: PropTypes.object }),
  loading: PropTypes.bool,
  error: PropTypes.string,
  /** The tool's accent, for the comparison charts. */
  color: PropTypes.string,
  isAdmin: PropTypes.bool,
  /** Admins: build the figures again now. */
  onRefresh: PropTypes.func,
  refreshing: PropTypes.bool,
  refreshError: PropTypes.string,
  /** Controlled filters (Analytics keeps them in the address). Left out, they are remembered in the browser. */
  filters: PropTypes.object,
  onFiltersChange: PropTypes.func,
  /** For tests: the clock. */
  now: PropTypes.number,
};

export default OutreachReport;
